import React from 'react';
import { RadioWithLabel, RadioInput, RadioLabel, Link, ColorSelection } from '@hpi-sox/hpstellar-core';
import { Typography } from '@hpi-sox/hpstellar-core/visId';

import PropTypes from 'prop-types';

import AdaptiveImage from '../../shared/components/adaptive-image';
import { Helpers } from '../../core/src/helpers';
import useInpChecked from '../../hooks/useInpChecked';

import './configure-radio.less';

function RadioButton({
    className,
    infoClassName,
    ariaLabel,
    name,
    label,
    title,
    info,
    colorHex,
    imageUrl,
    value,
    active,
    error,
    offerMessage,
    textSize = 'default',
    content,
    onChange,
    productName,
    to,
    linkText = 'View Details',
    direction = 'row',
    gtmActions,
    variant,
    checked,
    isInpOptimized,
    inputVariant = 'primary',
    colorSwatchProps,
    v2 = false,
    ...otherProps
}) {
    const containerClass = `configure-radio text-${textSize}${className ? ' ' + className : ''}${
        active ? ' active' : ''
    }`;
    const alt = productName ? `${productName}${label ? ' - ' + label : ''}` : '';
    const hasError = !!error;
    const inpCheckedData = useInpChecked(value, checked, !isInpOptimized);
    const { derivedCheck, setInpChecked } = inpCheckedData || {};
    const onChangeHandler = e => {
        try {
            const value = e?.target?.value;
            if (!isInpOptimized) {
                onChange(value);
            } else {
                if (typeof setInpChecked === 'function') {
                    setInpChecked(!checked);
                }
                setTimeout(() => {
                    onChange(value);
                }, 0);
            }
        } catch (e) {}
    };
    const isChecked = isInpOptimized ? derivedCheck : checked;

    return (
        <RadioWithLabel
            ariaLabel={ariaLabel || label}
            active={active}
            className={`${containerClass} ${variant}`}
            error={error}
            variant={variant}
            checked={isChecked}
            activeClassName="radio-active"
            {...otherProps}
            onKeyDown={e => {
                if (e.key === 'Enter') {
                    onChangeHandler({ target: { value } });
                }
            }}
        >
            <RadioLabel className={`radio-container ${direction}`} aria-label={`${title ? title + ' ' : ''}${label}`}>
                <div className={`radio-left-container`}>
                    <div className={`radio-left`}>
                        {imageUrl && <AdaptiveImage src={imageUrl} desktopWidth={80} alt={alt} />}
                        {colorHex && (
                            <ColorSelection
                                className={`color-swatch ${colorSwatchProps?.shape === 'square' ? 'square' : ''}`}
                                colors={[{ color: colorHex }]}
                                isPreview={true}
                            />
                        )}
                        <div className="radio-label">
                            {title && <b className={'radio-title'}>{title}</b>}
                            {!v2 ? label : <Typography variant="body1">{label}</Typography>}
                            {offerMessage && (
                                <div
                                    className="offer-message"
                                    dangerouslySetInnerHTML={Helpers.createMarkup(offerMessage)}
                                />
                            )}
                            {to && (
                                <>
                                    <br />
                                    <Link
                                        aria-label="aria-label placeholder"
                                        target="_blank"
                                        dense
                                        to={to}
                                        variation="body"
                                    >
                                        {linkText}
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                    {!v2 && info ? (
                        <div className={`radio-info${infoClassName ? ` ${infoClassName}` : ''}`}>{info}</div>
                    ) : (
                        info && (
                            <Typography
                                variant="body1"
                                weight={700}
                                className={`radio-info${infoClassName ? ` ${infoClassName}` : ''}`}
                            >
                                {info}
                            </Typography>
                        )
                    )}
                </div>
                <RadioInput
                    className={`configure-radio-input${v2 ? ' cto-alt-style' : ''}${hasError ? ' error-input' : ''}`}
                    value={value}
                    onChange={onChangeHandler}
                    gtmActions={gtmActions}
                    checked={isChecked}
                    variant={inputVariant}
                />
                {content && <div className="radio-custom">{content}</div>}
            </RadioLabel>
        </RadioWithLabel>
    );
}

RadioButton.propTypes = {
    /** title text to appear above the label */
    title: PropTypes.string,
    /** Display name for the label of the radio button */
    label: PropTypes.string,
    /** Additional info about radio option */
    info: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.element]),
    /** Value of the radio button */
    value: PropTypes.any.isRequired,
    /** Additional class names */
    className: PropTypes.string,
    /** Additional class names */
    infoClassName: PropTypes.string,
    /** custom aria label */
    ariaLabel: PropTypes.string,
    theme: PropTypes.oneOf(['light', 'dark']),
    /** Variant display types */
    variant: PropTypes.oneOf(['primary', 'secondary']),
    /** HEX value to add a color swatch to the ratio button */
    colorHex: PropTypes.string,
    /** Link to image to be displayed */
    imageUrl: PropTypes.string,
    /** Change font size of radio label */
    textSize: PropTypes.oneOf(['default', 'small']),
    /** custom content */
    content: PropTypes.element,
    broad: PropTypes.bool,
    /** onChange event of toggling radio button to active */
    onChange: PropTypes.func,
    direction: PropTypes.oneOf(['row', 'column']),
};

RadioButton.defaultProps = {
    theme: 'light',
    variant: 'primary',
    broad: true,
};

export default React.memo(RadioButton);
